import { FC, Fragment, useCallback, useState } from 'react'
import { AlgoliaIndex, MobileSearchFiltersLayout, SearchPagination } from 'search'
import { renderUntil } from 'shared/hocs/render'
import { media, styles } from 'shared/lib'
import styled from 'styled-components'

import MobileFilterButton from './MobileFilterButton'
import ConsumerSearchMobileFilterButton from './SearchFilters/NewFilters/MobileFilterButton'

import unionBy from 'lodash/unionBy'
import { useRouter } from 'next/router'
import { AggregationFilters, ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { CSSearchState } from 'shared/utils/search/cs-utils'
import ResultsCounter, { ResultsCounter as ConsumerSearchResultsCounter } from './ResultsCounter'
import { NewMobileFiltersLayout } from './SearchFilters/MobileSearchFiltersLayout'
import SearchGrid, { ConsumerSearchGrid } from './SearchGrid'
import { Pagination } from './SearchPagination'
import SearchSort, { SearchSort as ConsumerSearchSearchSort } from './SearchSort'

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${styles.mobilePageLayoutPadding};
  ${media.large`display: none;`}
`

const Layout = styled.div`
  padding: ${styles.mobilePageLayoutPadding};
`
interface MobileAlgoliaSearchGridLayoutProps {
  defaultRefinement: string
  hiddenFilters: Set<string>
  sortConfig: {
    value: AlgoliaIndex
    label: string
  }[]
}

interface MobileGridSearchLayout {
  consumerResults: ProductSearchResponse
  defaultRefinement: AlgoliaIndex
  handleQueryChange?: (attr: string, value: string | number) => void
  initialFilters: AggregationFilters
  initialSearchState: CSSearchState
  nbHits?: number
  sortConfig: {
    value: AlgoliaIndex
    label: string
  }[]
}

const MobileGridSearchLayout = ({
  consumerResults,
  defaultRefinement,
  hiddenFilters,
  initialFilters,
  initialSearchState,
  sortConfig,
}: MobileGridSearchLayout & MobileAlgoliaSearchGridLayoutProps) => {
  const router = useRouter()
  const [showFilterTakeOver, setShowFilterTakeOver] = useState(false)
  const handleFilterButtonClick = () => {
    setShowFilterTakeOver(!showFilterTakeOver)
  }
  const sizeCount = useCallback(() => {
    const mensSizes = router.query?.size_men?.split(',')?.length || 0
    const womensSizes = router.query?.size_women?.split(',')?.length || 0
    const youthSizes = router.query?.size_youth?.split(',')?.length || 0

    return mensSizes + womensSizes + youthSizes
  }, [router.query])

  const handleQueryChange = (attr: string, value: string | number) => {
    let newQuery = { ...(router?.query as Record<string, string>) }

    newQuery = {
      ...newQuery,
      [attr]: `${value}`,
    }

    if (attr !== 'page') {
      delete newQuery.page
    }

    if (newQuery[attr] === '') {
      delete newQuery[attr]
    }

    void router.push(
      `${window.location.pathname}?` + new URLSearchParams(newQuery).toString(),
      undefined,
      {
        shallow: true,
      },
    )
  }

  // get the silhouette list from here
  const newSilhouettesList = (() => {
    // get the current selected models from the initial search state
    const currentSelectedModels = initialSearchState?.refinementList?.silhouette
      ? initialSearchState.refinementList.silhouette.map((s: string) => ({ name: s, count: 1 }))
      : []

    if (
      initialSearchState?.refinementList?.collection_slugs?.[0] ||
      initialSearchState?.refinementList?.brand_name ||
      initialSearchState?.refinementList?.brand
    ) {
      // if there are any filters applied get the available models from the search
      // amd include any current selected model
      return unionBy(
        consumerResults?.aggregation?.filters?.silhouettesList || [],
        currentSelectedModels,
        'name',
      )
    } else {
      // else no filters are applied and we get all models
      // amd include any current selected model
      return unionBy(initialFilters?.silhouettesList || [], currentSelectedModels, 'name')
    }
  })()

  // here make sure the silhouette list is unchanging

  return (
    <Fragment>
      <ActionsWrapper>
        <ConsumerSearchMobileFilterButton onClick={handleFilterButtonClick} />
        <ConsumerSearchSearchSort
          items={sortConfig}
          currentRefinement={defaultRefinement}
          refine={(value) => {
            handleQueryChange?.('sortBy', value)
          }}
        />
      </ActionsWrapper>
      <Layout>
        <ConsumerSearchResultsCounter nbHits={consumerResults?.total} />
        <NewMobileFiltersLayout
          show={showFilterTakeOver}
          toggle={handleFilterButtonClick}
          filters={
            (router.query?.page ? initialFilters : consumerResults?.aggregation?.filters) || {}
          }
          silhouettesList={newSilhouettesList}
          hiddenFilters={hiddenFilters}
          isApparel={router.query?.slug?.includes('apparel')}
        />
        <ConsumerSearchGrid hits={consumerResults?.data || {}} sizeFilterCount={sizeCount()} />
        <Pagination
          currentRefinement={parseInt((router.query?.page as string) || '1')}
          nbPages={
            consumerResults?.data?.length > 0 ? Math.ceil((consumerResults?.total || 0) / 30) : 0
          }
          refine={(value) => {
            handleQueryChange('page', value)
          }}
        />
      </Layout>
    </Fragment>
  )
}

export const ConsumerSearchMobileGridLayout = renderUntil.large(MobileGridSearchLayout)

const MobileAlgoliaSearchGridLayout: FC<MobileAlgoliaSearchGridLayoutProps> = ({
  defaultRefinement,
  hiddenFilters,
  sortConfig,
}) => {
  const [showFilterTakeOver, setShowFilterTakeOver] = useState(false)
  const handleFilterButtonClick = () => {
    setShowFilterTakeOver(!showFilterTakeOver)
  }
  return (
    <Fragment>
      <ActionsWrapper>
        <MobileFilterButton hiddenFilters={hiddenFilters} onClick={handleFilterButtonClick} />
        <SearchSort items={sortConfig} defaultRefinement={defaultRefinement} />
      </ActionsWrapper>
      <Layout>
        <ResultsCounter />
        <MobileSearchFiltersLayout
          hiddenFilters={hiddenFilters}
          show={showFilterTakeOver}
          toggle={handleFilterButtonClick}
        />
        <SearchGrid />
        <SearchPagination />
      </Layout>
    </Fragment>
  )
}

MobileAlgoliaSearchGridLayout.displayName = 'MobileAlgoliaSearchGridLayout'

export default renderUntil.large(MobileAlgoliaSearchGridLayout)
